import { useState } from 'react';

import SendIcon from '@common/assets/icons/send.svg?react';
import { Button } from '@common/ui/buttons/Button';
import { DynamicImage } from '@common/ui/DynamicImage';
import { TextArea } from '@common/ui/inputs/TextArea';
import { useEventEmit } from '@common/utils/events';
import { getApiErrorInfo, isRateLimitedError } from '@common/utils/fetcher';

import {
  checkNote,
  invalidNoteReasonOf,
  MATCHMAKER_NOTE_MAX_LENGTH,
  type MatchmakerFriend,
  NOTE_REFUSAL_TEXT,
  refusalFromError,
  useMatchmakerDeckStore,
  writeSwipeRemaining,
  yearsWord,
} from '@entities/matchmaker';

import { analytics } from '@services/AnalyticsService';
import { usePostMatchmakerRecommendation } from '@services/hooks/MatchmakerService';
import {
  MATCHMAKER_ERROR_CODES,
  type PostMatchmakerRecommendationResponse,
} from '@services/MatchmakerService';
import { type UserTextRefusal } from '@services/types';
import { type User } from '@services/UsersService';

import styles from './RecommendSheet.module.scss';
import { SheetOverlay } from '../SheetOverlay';

export type RecommendSheetProps = {
  card: User;
  friend: MatchmakerFriend;
  onClose: () => void;
  onSent: (recommendation: PostMatchmakerRecommendationResponse) => void;
};

const RATE_LIMITED_NOTICE = {
  title: 'Слишком много действий',
  description: 'Попробуйте ещё раз чуть позже',
};

/** Refusals that end with the card dropped from the deck, and their notice. */
const DROPPING_REFUSALS: Record<string, string> = {
  [MATCHMAKER_ERROR_CODES.alreadyRecommended]: 'Вы уже советовали эту анкету',
  [MATCHMAKER_ERROR_CODES.candidateUnavailable]: 'Анкета недоступна',
  [MATCHMAKER_ERROR_CODES.candidateCap]: 'Эту анкету сегодня уже советовали',
};

/** «Посоветовать анкету» (board 3.3): the note and the send. */
export const RecommendSheet = ({
  card,
  friend,
  onClose,
  onSent,
}: RecommendSheetProps) => {
  const emit = useEventEmit();
  const removeCard = useMatchmakerDeckStore((state) => state.removeCard);
  const refuse = useMatchmakerDeckStore((state) => state.refuse);

  const [note, setNote] = useState('');
  const [noteRefusal, setNoteRefusal] = useState<UserTextRefusal | null>(null);

  const {
    trigger: triggerPostMatchmakerRecommendation,
    isMutating: isMutatingPostMatchmakerRecommendation,
  } = usePostMatchmakerRecommendation();

  const handleRefusal = (error: unknown) => {
    const refusedNoteReason = invalidNoteReasonOf(error);

    if (refusedNoteReason) {
      setNoteRefusal(refusedNoteReason);

      return;
    }

    if (isRateLimitedError(error)) {
      emit('info', RATE_LIMITED_NOTICE);

      return;
    }

    const { code } = getApiErrorInfo(error);
    const droppingNotice = code ? DROPPING_REFUSALS[code] : undefined;

    if (droppingNotice) {
      emit('info', { title: droppingNotice });
      removeCard(card.id);
      onClose();

      return;
    }

    if (code === MATCHMAKER_ERROR_CODES.dailyCap) {
      emit('info', {
        title: 'На сегодня рекомендации закончились',
        description: 'Возвращайтесь завтра',
      });
      onClose();

      return;
    }

    const refusal = refusalFromError(error);

    if (refusal) {
      refuse(refusal);
      onClose();

      return;
    }

    emit('error', {
      title: 'Не удалось отправить',
      description: 'Попробуйте позже',
    });
  };

  const handleSend = async () => {
    const text = note.trim();
    const localRefusal = text ? checkNote(text) : null;

    if (localRefusal) {
      setNoteRefusal(localRefusal);

      return;
    }

    try {
      const recommendation = await triggerPostMatchmakerRecommendation({
        candidate_user_id: card.id,
        friend: { sex: friend.sex, age: friend.age, city: friend.city },
        ...(text ? { note: text } : {}),
      });

      writeSwipeRemaining(recommendation.remaining);
      analytics.trackMmRecommendSent(!!text);
      onSent(recommendation);
    } catch (error) {
      handleRefusal(error);
    }
  };

  return (
    <SheetOverlay
      testId="recommend-sheet"
      label="Посоветовать анкету"
      isDismissible={!isMutatingPostMatchmakerRecommendation}
      onDismiss={onClose}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>Посоветовать анкету</h2>
        <p className={styles.subtitle}>
          Добавь пару слов от себя — друг увидит их в сообщении
        </p>
      </header>

      <div className={styles.body}>
        <div
          className={styles.candidate}
          data-test-id="recommend-sheet-candidate"
        >
          <DynamicImage
            testId="recommend-sheet-candidate-photo"
            src={card.photos[0] ?? ''}
            alt=""
            className={styles.photo}
          />
          <div className={styles.candidateText}>
            <div className={`${styles.name} ym-hide-content`}>
              {card.username}, {card.age}
            </div>
            <div
              className={styles.details}
              data-test-id="recommend-sheet-candidate-details"
            >
              {card.city} · для друга {friend.age} {yearsWord(friend.age)}
            </div>
          </div>
        </div>

        <div>
          <TextArea
            className="ym-disable-keys"
            placeholder="Записка другу"
            maxLength={MATCHMAKER_NOTE_MAX_LENGTH}
            value={note}
            error={noteRefusal ? NOTE_REFUSAL_TEXT[noteRefusal] : undefined}
            onChange={(event) => {
              setNote(event.target.value);
              setNoteRefusal(null);
            }}
          />
          <div className={styles.hint}>
            <span>Необязательно · без ссылок</span>
            <span data-test-id="recommend-sheet-note-counter">
              {note.length} / {MATCHMAKER_NOTE_MAX_LENGTH}
            </span>
          </div>
        </div>

        <p className={styles.footer}>
          Друг увидит анкету, когда откроет ссылку. Человек на анкете не узнает
          о рекомендации.
        </p>
      </div>

      <div className={styles.buttons}>
        <Button
          testId="recommend-sheet-send-button"
          variant="actionPrimary"
          loading={isMutatingPostMatchmakerRecommendation}
          onClick={handleSend}
        >
          <SendIcon />
          Отправить другу в Telegram
        </Button>
        <Button
          testId="recommend-sheet-cancel-button"
          variant="actionSecondary"
          disabled={isMutatingPostMatchmakerRecommendation}
          onClick={onClose}
        >
          Отмена
        </Button>
      </div>
    </SheetOverlay>
  );
};
